<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Folder, useFilingStore } from '@/modules/filing/stores/filing-store'
import { useModal } from '@/modules/shared/composables/use-modal'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { useVuelidate } from '@vuelidate/core'
import { VButton, VIcon, VTextField, VModal, VSection, VLoadingSection } from '@/modules/shared/components'
import VFolderTree from './VFolderTree.vue'
import { required } from '@vuelidate/validators'
import VSelect from '@/modules/investing/components/v-select.vue'
import { downloadAndSave } from '@/modules/filing/utils/utils'
import { debounce, isNil } from 'lodash'
import VSearchResults from './VSearchResults.vue'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { stringifyKey } from '@/modules/core/stores/kv'
import DropZone from './DropZone.vue'

const props = defineProps<{
  rootFolder?: Folder | null
  shareholders: any
  individual_id?: string
  other_entity_id?: string
  skeleton?: boolean
  shareholderCids?: string[]
  entity?: any | null
}>()

const authStore = useAuthStore()

const selectedKeys = ref<string[]>([])
const currentFolder = ref(null)
const folderStack = ref<Folder[]>([])
const loadedFolders = ref<Set<string>>(new Set())
const draggedFolder = ref(null)
const loading = ref(false)
const downloadingFiles = ref(false)
const searchTerm = ref('')
const isSearching = ref(false)
const sortState = ref({ column: 'name', direction: 'asc' as 'asc' | 'desc' })

const filingStore = useFilingStore()
const workspaceStore = useWorkspaceStore()

let draggedFile = null

const folderBreadcrumbParts = computed(() => {
  const names = folderStack.value.filter(Boolean).map((folder) => ({
    name: folder.parent_id === null ? 'Root' : folder.name,
    isCurrent: false,
  }))

  if (currentFolder.value && folderStack.value.length > 0) {
    names.push({ name: currentFolder.value.name, isCurrent: true })
  }
  return names
})

const fetchAll = async () => {
  try {
    const folderParams: any = {
      parent_id: props.rootFolder?.id,
      individual_id: props.individual_id,
      other_entity_id: props.other_entity_id,
    }
    if (props.shareholderCids && props.shareholderCids.length > 0) {
      folderParams.shareholder_cids = props.shareholderCids
    }
    await filingStore.fetchFolders(folderParams)
    await filingStore.fetchFiles(props.rootFolder?.id)
    currentFolder.value = props.rootFolder
  } catch (error) {
    console.error('Error fetching folders and files:', error)
  }
}

const toggleSort = (column: string) => {
  if (sortState.value.column === column) {
    sortState.value.direction = sortState.value.direction === 'asc' ? 'desc' : 'asc'
  } else {
    sortState.value = { column, direction: 'asc' }
  }
}

const handleSearch = debounce(async () => {
  if (searchTerm.value.trim().length === 0) {
    clearSearch(false)
    return
  }
  try {
    loading.value = true
    clearSearch(true)
    await filingStore.search(searchTerm.value, {
      parent_id: currentFolder.value?.id,
      individual_id: props.individual_id,
      other_entity_id: props.other_entity_id,
    })
  } catch (error) {
    console.error('Error during search:', error)
  } finally {
    loading.value = false
  }
}, 500)

const clearSearch = (search: boolean) => {
  isSearching.value = search
  folderStack.value = []
  currentFolder.value = props.rootFolder || null
}

// Pass the submit function back to the parent
defineExpose({
  fetchAll,
})

// TODO: keyboard shortcuts:
// - cmd + a: select all
// - cmd + d: delete selected
// - cmd + c: copy selected
// - cmd + v: paste copied
// - cmd + z: undo
// - cmd + y: redo

// TODO: context menu:
// - right click to open context menu
// - context menu to create new folder
// - context menu to create new folder with selected
// - context menu to delete selected
// - context menu to duplicate selected
// - context menu to paste copied
// - context menu to rename folder
// - context menu to download selected

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const isAggregateView = computed(() => props.individual_id || props.other_entity_id)
const isAdminToInvestor = computed(() => {
  if (isNil(props.entity)) return false
  if (authStore.is_site_or_group_admin) return false
  if (props.rootFolder?.fileable_type !== 'InvestorSet') return false
  if (authStore.current_user?.managed_investor_set_ids?.includes(props.rootFolder?.fileable_id)) return false
  if (props.entity.shareholders.some((shareholder) => shareholder.id === authStore.current_user.investor_id))
    return false
  return !!props.entity.shareholders.some((shareholder) =>
    authStore.current_user.managed_investor_set_ids.includes(shareholder.id),
  )
})
const isGroupReadOnly = computed(() => {
  return props.rootFolder?.fileable_type === 'Group' && !authStore.is_site_or_group_admin
})
const isReadOnly = computed(() => !!isAggregateView.value || isAdminToInvestor.value || isGroupReadOnly.value)

///////////////////////////////////////////////////////////////////////////////
// Folder Form
///////////////////////////////////////////////////////////////////////////////

const folderInitialState = {
  id: null,
  name: '',
  access_level: '',
  parent_id: null,
  assigned_shareholders: [],
  fileable_type: null,
  fileable_id: null,
}
const folderRules = {
  name: { required },
  access_level: { required },
  fileable_type: { required },
  fileable_id: { required },
}
const folderForm = ref({ ...folderInitialState })
const v$ = useVuelidate(folderRules, folderForm, { $lazy: true })

const shareholderOptions = computed(() => {
  let parentFolder = filingStore.folders.find((f) => f.id === folderForm.value.parent_id)
  parentFolder = parentFolder || props.rootFolder

  let shareholders = props.shareholders
  if (parentFolder.assigned_shareholders.length > 0) {
    shareholders = shareholders.filter((shareholder) =>
      parentFolder.assigned_shareholders.some(
        (assigned) => assigned.id === shareholder.id && assigned.type === shareholder.type,
      ),
    )
  }

  const options = shareholders.map((shareholder) => ({
    value: stringifyKey([shareholder.id, shareholder.type]),
    label: shareholder.name,
  }))

  return options
})
const accessLevelOptions = computed(() => {
  let adminLabel = 'Entity Admins'

  if (props.rootFolder?.fileable_type === 'Investor') {
    adminLabel = 'Investor Admins'
  }

  const allOptions = [
    { label: 'Investors', value: 'investor' },
    { label: adminLabel, value: 'admin' },
    { label: 'Organization Admins', value: 'group_admin' },
    { label: 'Site Admins', value: 'site_admin' },
  ]

  const roleHierarchy = {
    investor: ['investor'],
    admin: ['investor', 'admin'],
    group_admin: ['investor', 'admin', 'group_admin'],
    site_admin: ['investor', 'admin', 'group_admin', 'site_admin'],
  }

  if (props.rootFolder?.fileable_type === 'Group') {
    roleHierarchy.group_admin = ['group_admin']
    roleHierarchy.site_admin = ['investor', 'group_admin', 'site_admin']
  }

  if (
    props.rootFolder?.fileable_type === 'InvestorSet' &&
    authStore.current_user?.managed_investor_set_ids?.includes(props.rootFolder?.fileable_id)
  ) {
    roleHierarchy.investor = ['investor', 'admin']
  }

  if (
    props.rootFolder?.fileable_type === 'Investor' &&
    authStore.current_user?.managed_individual_ids?.includes(props.rootFolder?.fileable_id)
  ) {
    roleHierarchy.investor = ['investor', 'admin']
  }

  return allOptions.filter((option) => roleHierarchy[authStore.current_user.role].includes(option.value))
})
const isAccessLevelEnabled = computed(() => {
  if (accessLevelOptions.value.length === 1) return false
  if (folderForm.value.parent_id === props.rootFolder?.id) return true
  return false
})
const isShareholderOptionsVisible = computed(() => {
  if (folderForm.value.access_level !== 'investor') return false
  if (shareholderOptions.value.length === 0) return false
  if (!folderForm.value.parent_id) return false

  let parentFolder = filingStore.folders.find((f) => f.id === folderForm.value.parent_id)
  parentFolder = parentFolder || props.rootFolder

  if (parentFolder.access_level !== 'investor') return false
  if (parentFolder.system_folder === 'investors') return true
  if (parentFolder.fileable_type === 'Investor') return false

  return parentFolder.id === props.rootFolder.id
})
const autoAssignShareholder = computed(() => {
  if (folderForm.value.access_level !== 'investor') return false
  if (isShareholderOptionsVisible.value) return false

  let parentFolder = filingStore.folders.find((f) => f.id === folderForm.value.parent_id)
  parentFolder = parentFolder || props.rootFolder

  return parentFolder.assigned_shareholders.length > 0
})

const showDropZone = computed(() => {
  if (isSearching.value || isReadOnly.value) return false
  if (currentFolder.value.id === props.rootFolder?.id) {
    return currentFolder.value.fileable_type !== 'InvestorSet'
  }
  return true
})

///////////////////////////////////////////////////////////////////////////////
// Folder Form Actions
///////////////////////////////////////////////////////////////////////////////
const createFolderModal = useModal()

const addFolder = (parentFolder = null) => {
  if (isReadOnly.value) return
  v$.value.$reset()

  parentFolder = parentFolder || currentFolder.value

  folderForm.value = {
    ...folderInitialState,
    fileable_type: parentFolder.fileable_type,
    fileable_id: parentFolder.fileable_id,
    parent_id: parentFolder.id,
    access_level: parentFolder.access_level,
  }
  createFolderModal.open()
}

const editFolder = (folder) => {
  if (!folder) return
  if (isReadOnly.value) return
  v$.value.$reset()

  const folderShareholders = folder.assigned_shareholders.map((shareholder) => ({
    value: stringifyKey([shareholder.id, shareholder.type]),
    label: shareholder.name,
  }))

  folderForm.value = {
    id: folder.id,
    name: folder.name,
    parent_id: folder.parent_id,
    access_level: folder.access_level,
    fileable_type: folder.fileable_type,
    fileable_id: folder.fileable_id,
    assigned_shareholders: shareholderOptions.value
      .filter((opt) => folderShareholders.some((shareholder) => shareholder.value === opt.value))
      .map((assignee) => assignee.value),
  }
  createFolderModal.open()
}

const buildFolderPayload = () => {
  const payload = {
    id: folderForm.value.id,
    name: folderForm.value.name,
    parent_id: folderForm.value.parent_id,
    group_id: workspaceStore.current.id,
    access_level: folderForm.value.access_level,
    fileable_id: folderForm.value.fileable_id || null,
    fileable_type: folderForm.value.fileable_type,
    folders_shareholders_attributes: [],
  }

  if (folderForm.value.access_level === 'investor') {
    let shareholdersPayload = folderForm.value.assigned_shareholders
    if (autoAssignShareholder.value) shareholdersPayload = shareholderOptions.value.map((opt) => opt.value)

    const assignedShareholders = shareholdersPayload.map((kv_id) => {
      const [shareholder_id, shareholder_type] = JSON.parse(kv_id)
      return { shareholder_id, shareholder_type }
    })
    payload.folders_shareholders_attributes = [...assignedShareholders]
  }

  return { folder: payload }
}

const submitFolder = async () => {
  if (isReadOnly.value) return
  const valid = await v$.value.$validate()
  if (!valid) return

  const payload = buildFolderPayload()

  try {
    if (folderForm.value.id) {
      // Edit mode: Update the folder
      await filingStore.updateFolder(folderForm.value.id, payload)
    } else {
      // Create mode: Add a new folder
      await filingStore.addFolder(payload)
    }
    await loadFolderContents(payload.folder.parent_id, true)
    if (payload.folder.id) filingStore.updateAccessLevelOfDescendants(payload.folder.id)
    createFolderModal.close()
  } catch (error) {
    console.error(folderForm.value.id ? 'Folder update failed:' : 'Folder creation failed:', error)
  }
}

///////////////////////////////////////////////////////////////////////////////
// Table Actions
///////////////////////////////////////////////////////////////////////////////

const openFolder = async (folder) => {
  isSearching.value = false

  folderStack.value.push(currentFolder.value)
  currentFolder.value = folder
  selectedKeys.value = []

  try {
    loading.value = true
    await loadFolderContents(folder.id)
  } catch (error) {
    console.error('Error fetching folders and files:', error)
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  currentFolder.value = folderStack.value.pop() || null
}

const onSelectRow = (key: string) => {
  if (selectedKeys.value.includes(key)) {
    selectedKeys.value = selectedKeys.value.filter((k) => k !== key)
  } else {
    selectedKeys.value = [key]
  }
}

const loadFolderContents = async (folderId: string, loadOverride = false) => {
  if (loadedFolders.value.has(folderId) && !loadOverride) return

  try {
    await filingStore.fetchFolders({
      parent_id: folderId,
      individual_id: props.individual_id,
      other_entity_id: props.other_entity_id,
      shareholder_cids: props.shareholderCids,
    })
    await filingStore.fetchFiles(folderId)
    loadedFolders.value.add(folderId)
  } catch (err) {
    console.error('Failed to lazy-load folder contents:', err)
  }
}

const onFileDragStart = (file) => {
  if (isReadOnly.value) return
  draggedFile = file
}

const onFolderDragStart = (folder) => {
  if (isReadOnly.value) return
  draggedFolder.value = folder
}

const validateDrag = (draggedFolder, targetFolder): string | null => {
  if (draggedFolder.system_folder) {
    return 'You cannot move this folder as it was created by the system.'
  }
  if (draggedFolder.access_level !== targetFolder.access_level) {
    return 'You cannot move this folder as the permissions do not match.'
  }
  return null
}

const onItemDrop = async (event, targetFolder) => {
  if (isReadOnly.value) return false
  if (draggedFolder.value) {
    if (draggedFolder.value.id === targetFolder.id) {
      draggedFolder.value = null
      return false
    }
    const errorMessage = validateDrag(draggedFolder.value, targetFolder)
    if (errorMessage) {
      window.alert(errorMessage)
      draggedFolder.value = null
      return false
    }

    try {
      await filingStore.moveFolder(draggedFolder.value.id, targetFolder.id)
    } catch (error) {
      console.error('Error moving folder:', error)
    } finally {
      draggedFolder.value = null
      return true
    }
  } else if (draggedFile) {
    try {
      await filingStore.moveFile(draggedFile.id, targetFolder.id)
    } catch (err) {
      console.error('Failed to move file:', err)
    } finally {
      draggedFile = null
      return true
    }
  } else {
    const files = event.dataTransfer?.files
    if (!files?.length) return false

    for (const file of files) {
      await filingStore.addFile(
        file,
        targetFolder.id,
        currentFolder.value?.fileable_id,
        currentFolder.value?.fileable_type,
      )
    }
    return true
  }
}

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const downloadAll = async () => {
  try {
    downloadingFiles.value = true
    const folderId = currentFolder.value?.id || null
    await downloadAndSave(filingStore.downloadAll(folderId, props.individual_id, props.other_entity_id), 'files.zip')
  } catch (error) {
    console.error('Error downloading files:', error)
  } finally {
    downloadingFiles.value = false
  }
}

// -----------------------------------------------------------------------------
// Drop zone
// -----------------------------------------------------------------------------

import { useFileDialog } from '@vueuse/core'

const dropZoneState = ref<'default' | 'uploading'>('default')

const uploadFiles = async (fileList: FileList) => {
  dropZoneState.value = 'uploading'

  const fileArray = Array.from(fileList) as File[]

  for (const file of fileArray) {
    await filingStore.addFile(
      file,
      currentFolder.value?.id,
      currentFolder.value?.fileable_id,
      currentFolder.value?.fileable_type,
    )
  }

  dropZoneState.value = 'default'
}

const handleDropZoneDrop = async (event) => {
  if (isReadOnly.value) return

  const files = event.dataTransfer?.files
  if (!files?.length) return

  uploadFiles(files)
}

const { files: fd_files, open: openFileDialog } = useFileDialog()

watch(fd_files, async (fileList) => {
  await uploadFiles(fileList)
})
</script>

<template>
  <div v-if="!skeleton">
    <div class="mb-6 flex items-center justify-between">
      <VTextField
        v-model="searchTerm"
        @input="handleSearch"
        type="search"
        :placeholder="'Search...'"
        inputClass="h-8 mt-0"
        class="mr-4 flex-grow"
      />
      <div class="flex items-center space-x-2">
        <VButton @click="() => addFolder()" class="mt-1" v-if="!isReadOnly">New Folder</VButton>
        <VButton @click="downloadAll" class="mt-1" :loading="downloadingFiles" v-if="!!currentFolder">
          Download All
        </VButton>
      </div>
    </div>

    <div class="mt-0">
      <div class="table" :class="{ hasBreadcrumb: folderBreadcrumbParts.length }">
        <!-- Breadcrumb -->
        <div v-if="folderBreadcrumbParts.length" class="thead breadcrumb">
          <div class="col-span-5 rounded-t-lg bg-gray-100 text-sm">
            <div class="flex items-center space-x-2">
              <VButton @click="goBack" v-if="folderStack.length" variant="invisible">
                <VIcon name="ChevronDown" class="h-4 w-4 rotate-90" />
              </VButton>
              <div class="flex items-center space-x-2">
                <div
                  v-for="(part, index) in folderBreadcrumbParts"
                  :key="index"
                  class="flex items-center space-x-2"
                  :class="index === folderBreadcrumbParts.length - 1 ? 'text-gray-900' : 'text-gray-500'"
                >
                  <span v-if="index > 0" class="mx-1"> / </span>
                  <VIcon
                    name="FileFolderOpen"
                    size="14"
                    :class="index === folderBreadcrumbParts.length - 1 ? 'opacity-75' : 'opacity-50'"
                  />
                  <span
                    :class="{
                      'font-semibold': index === folderBreadcrumbParts.length - 1,
                      'font-medium': index !== folderBreadcrumbParts.length - 1,
                      'text-gray-600': index !== folderBreadcrumbParts.length - 1,
                    }"
                    >{{ part.name }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Columns -->
        <div
          class="thead"
          :draggable="true"
          @dragenter="console.log('dragenter')"
          @dragover.prevent
          @drop.prevent="onItemDrop($event, currentFolder)"
        >
          <div class="th" @click="toggleSort('name')">
            <span class="cursor-pointer">Name</span>
            <VIcon
              :class="[
                '-mt-[2px] ml-1 inline-block h-4 text-gray-500',
                sortState.direction === 'asc' ? '' : 'rotate-180',
              ]"
              name="ArrowNarrowUp"
              v-show="sortState.column === 'name'"
            />
          </div>
          <div class="th" @click="toggleSort('updated_at')">
            <span class="cursor-pointer">Date Uploaded</span>
            <VIcon
              :class="[
                '-mt-[2px] ml-1 inline-block h-4 text-gray-500',
                sortState.direction === 'asc' ? '' : 'rotate-180',
              ]"
              name="ArrowNarrowUp"
              v-show="sortState.column === 'updated_at'"
            />
          </div>
          <div class="th" @click="toggleSort('access_level')">
            <span class="cursor-pointer">Permission</span>
            <VIcon
              :class="[
                '-mt-[2px] ml-1 inline-block h-4 text-gray-500',
                sortState.direction === 'asc' ? '' : 'rotate-180',
              ]"
              name="ArrowNarrowUp"
              v-show="sortState.column === 'access_level'"
            />
          </div>
          <div class="th" @click="toggleSort('created_by')">
            <span class="cursor-pointer">Created By</span>
            <VIcon
              :class="[
                '-mt-[2px] ml-1 inline-block h-4 text-gray-500',
                sortState.direction === 'asc' ? '' : 'rotate-180',
              ]"
              name="ArrowNarrowUp"
              v-show="sortState.column === 'name'"
            />
          </div>
          <div class="th"></div>
        </div>

        <VLoadingSection v-if="loading" class="mb-4 mt-4" />
        <div class="tbody" v-else>
          <VSearchResults
            v-if="isSearching"
            :openFolder="openFolder"
            :addFolder="addFolder"
            :editFolder="editFolder"
            :onFolderDragStart="onFolderDragStart"
            :onItemDrop="onItemDrop"
            :onSelectRow="onSelectRow"
            :selectedKeys="selectedKeys"
            :onFileDragStart="onFileDragStart"
            :sortState="sortState"
            :isReadOnly="isReadOnly"
          />
          <VFolderTree
            v-else
            :loadFolderContents="loadFolderContents"
            :openFolder="openFolder"
            :onItemDrop="onItemDrop"
            :onFileDragStart="onFileDragStart"
            :onFolderDragStart="onFolderDragStart"
            :addFolder="addFolder"
            :editFolder="editFolder"
            :folder="currentFolder"
            :onSelectRow="onSelectRow"
            :level="0"
            :selectedKeys="selectedKeys"
            :sortState="sortState"
            :isReadOnly="isReadOnly"
          />
        </div>

        <!-- Drop zone -->
        <DropZone
          v-if="showDropZone"
          @click="openFileDialog()"
          @drop.prevent="handleDropZoneDrop"
          @dragover.prevent
          :state="dropZoneState"
        />
      </div>
    </div>
    <VModal :modalStore="createFolderModal">
      <template #main>
        <VSection :label="folderForm.id ? 'Edit Folder' : 'Create New Folder'">
          <div class="space-y-3">
            <VTextField v-model="folderForm.name" :label="'Folder Name'" property="name" :v$="v$" />
            <VSelect
              v-model="folderForm.access_level"
              :label="'Permission'"
              :options="accessLevelOptions"
              property="access_level"
              :disabled="!isAccessLevelEnabled"
              :v$="v$"
            />
            <VSelect
              v-if="isShareholderOptionsVisible"
              v-model="folderForm.assigned_shareholders"
              label="Individual Investor"
              :options="shareholderOptions"
              property="assigned_shareholders"
              :v$="v$"
              placeholder="All"
              :multi="true"
            />
          </div>
        </VSection>
      </template>
      <template #footer>
        <VButton @click="submitFolder" variant="primary" size="lg" class="w-full">{{
          folderForm.id ? 'Update Folder' : 'Create Folder'
        }}</VButton>
      </template>
    </VModal>
  </div>
  <VLoadingSection
    v-else
    class="my-40"
    title="Preparing Your Information"
    description="Please wait a moment while we set things up for you. Your data is being processed to ensure everything runs
  smoothly."
  />
</template>

<style scoped>
.table {
  @apply w-full select-none rounded-md border-none bg-white text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-700/20;
}

.thead {
  @apply sticky top-0 z-30 grid grid-cols-5 rounded-t-lg border-b border-gray-200 text-[12px] text-xs font-semibold tracking-wide text-gray-700 backdrop-blur;
  @apply [grid-template-columns:30%_20%_20%_20%_10%];
}

.thead.breadcrumb {
}

.thead:not(.breadcrumb) {
  @apply uppercase;
}

.th {
  @apply px-4 py-3;
}

.tbody {
  @apply grid grid-cols-5 [grid-template-columns:40%_20%_20%_10%];
}
</style>
